import React, { Component } from 'react';
import classNames from 'classnames';
import { flowRight } from 'lodash';
import { type ConnectedProps } from 'react-redux';
import { TooltipPortal } from '../../context/tooltip-context';
import withFontClassName from '../../hoc/with-font-class-name';
import { isSite } from '../../store/basic-params/basic-params-selectors';
import { connect } from '../runtime-context';
import styles from './button-tooltip.scss';

type OwnProps = {
  target?: Element | null;
  isVisible?: boolean;
  children?: React.ReactNode;
  contentFontClassName?: string;
};

type Props = OwnProps & ConnectedProps<typeof connector>;

type State = {
  position: { top: number; left: number };
  arrowPosition?: string;
};

class ButtonTooltip extends Component<Props, State> {
  container: HTMLDivElement | null = null;

  target: Element | null = null;

  state: State = {
    position: { top: 0, left: 0 },
  };

  componentDidUpdate(prevProps: Props) {
    const { isVisible, isSite } = this.props;

    if (isVisible !== prevProps.isVisible) {
      if (isVisible) {
        isSite ? this.showComponent() : this.showComponentInEditor();
      }
    }
  }

  showComponentInEditor() {
    if (!this.container || !this.props.target) {
      return;
    }

    const topMargin = 12;
    const leftMargin = 19;
    const rect = this.container!.getBoundingClientRect()!;
    const targetRect = this.props.target.getBoundingClientRect();

    const top = targetRect.top - (topMargin + rect.height);
    let left = targetRect.right - targetRect.width / 2 + leftMargin - rect.width;
    let arrowPosition;

    if (left < 0) {
      left = targetRect.right - targetRect.width / 2 - leftMargin;
      arrowPosition = styles.arrowleft;
    }

    this.setState({ position: { top, left }, arrowPosition });
  }

  showComponent() {
    if (!this.container || !this.props.target) {
      return;
    }

    const rect = this.container!.getBoundingClientRect()!;
    const targetRect = this.props.target.getBoundingClientRect()!;
    const rootRect = document.getElementById(this.props.componentId)!.getBoundingClientRect()!!;

    const relativeRect = {
      top: targetRect.top - rootRect.top,
      right: targetRect.right - rootRect.left,
      width: targetRect.width,
    };

    const top = relativeRect.top - (12 + rect.height);
    let left = relativeRect.right - relativeRect.width / 2 + 19 - rect.width;
    let arrowPosition;

    if (left < 0) {
      left = relativeRect.right - relativeRect.width / 2 - 19;
      arrowPosition = styles.arrowleft;
    }

    this.setState({ position: { top, left }, arrowPosition });
  }

  setContainer = (container: HTMLDivElement | null) => {
    this.container = container;
  };

  render() {
    const { contentFontClassName, children, isVisible } = this.props;
    const { position, arrowPosition } = this.state;

    const containerClassName = classNames(
      styles.bubble,
      contentFontClassName,
      arrowPosition,
      'blog-text-color',
      'blog-card-border-color',
      'blog-card-background-color',
    );
    const style = {
      visibility: isVisible ? ('visible' as const) : ('hidden' as const),
      ...position,
    };

    return (
      <TooltipPortal>
        <div className={containerClassName} style={style} ref={this.setContainer}>
          <div className={styles.content} data-hook="tooltip">
            {children}
          </div>
          <div className={styles.triangleWrapper}>
            <div
              className={classNames(
                styles.triangle,
                'blog-card-border-color',
                'blog-card-background-color',
              )}
            />
          </div>
        </div>
      </TooltipPortal>
    );
  }
}

const connector = connect((state, _ownProps: OwnProps, _actions, host) => ({
  isSite: isSite(state),
  componentId: host.id,
}));

export default flowRight(withFontClassName, connector)(ButtonTooltip);
